import classNames from "classnames";
import { memo, useEffect, useRef } from "react";
import { Flex } from "./flex";
import * as optInputCss from "./otp-input.module.css";
import { TextInput } from "./text-input";

export const OtpInput = memo(function OtpInput({
  values,
  onChange,
  numeric = false,
  fullWidth = false,
}: {
  values: string[];
  onChange: (newValues: string[]) => void;
  numeric?: boolean;
  /** Squares that share the width of the row, instead of the fixed cells. */
  fullWidth?: boolean;
}) {
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[]>(
    [],
  );

  useEffect(() => {
    if (values.every((value) => value === "")) {
      inputRefs.current[0]?.focus();
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, inputRefs.current]);

  return (
    <Flex
      dir="row"
      gap={fullWidth ? "lg" : "3xl"}
      justify="center"
      w={fullWidth ? "full" : undefined}
    >
      {values.map((value, i) => {
        const field = (
          <TextInput
            fullwidth={fullWidth}
            inputMode="numeric"
            maxLength={1}
            onChange={(newValue) => {
              if (numeric && !/^\d*$/.test(newValue)) {
                return;
              }

              const newValues = [...values];
              newValues[i] = newValue;
              onChange(newValues);

              if (
                newValue &&
                i < values.length - 1 &&
                inputRefs.current[i + 1] instanceof HTMLInputElement
              ) {
                inputRefs.current[i + 1]?.focus();
              }
            }}
            onKeyDown={(e) => {
              if (
                (e.key === "Backspace" || e.key === "Delete") &&
                !value &&
                i > 0
              ) {
                inputRefs.current[i - 1]?.focus();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              let paste = e.clipboardData
                .getData("text")
                .slice(0, values.length - i);

              if (numeric) {
                paste = paste.replace(/\D/g, "");
              }

              const newValues = [...values];

              for (const [index, char] of paste.split("").entries()) {
                newValues[i + index] = char;
              }

              onChange(newValues);

              if (i + paste.length < values.length) {
                inputRefs.current[i + paste.length]?.focus();
              } else {
                inputRefs.current[values.length - 1]?.focus();
              }
            }}
            ref={(el) => (inputRefs.current[i] = el)}
            value={value}
          />
        );
        if (fullWidth) {
          return (
            <Flex
              basis={0}
              className={classNames(
                optInputCss.inputContainer,
                optInputCss.fullWidth,
              )}
              data-web="OtpInput"
              gap="none"
              grow={1}
              height="auto"
              key={i}
              minW="none"
              shrink={1}
            >
              {field}
            </Flex>
          );
        }
        return (
          <div
            className={classNames(optInputCss.inputContainer, optInputCss.cell)}
            data-web="OtpInput"
            key={i}
          >
            {field}
          </div>
        );
      })}
    </Flex>
  );
});
